import { isDefined } from '@sgme/fp';
import type { QuoteInfo, ScheduleRunStatus } from 'apiConnector/scheduledRun/model';
import type {
  IScheduledQuotesBlotterItem,
  IScheduledQuotesBlotterRequest,
} from 'apiConnector/scheduledRun/scheduledQuotesBlotter';
import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { createSelector } from 'reselect';
import type { IBlotterColumnDefinition, Sort } from 'state/blotters/common/blotter.model';
import { EDITABLE_SCHEDULED_RUN_BLOTTER_ID } from 'state/blotters/scheduledQuotes';
import type { IScheduledQuotesBlotterFilters } from 'state/blotters/scheduledQuotes/scheduledQuotesBlotter.model';
import type { ScheduledRun } from 'state/scheduledRuns/state';
import type { AppState } from 'state/state.model';
import { getUserPreferencesLanguage } from 'state/userPreferences/userPreferences.selectors';
import { translateText } from 'utils/lang/lang';
import { asSuccess } from 'utils/status';

function getScheduledQuotesBlotterState(state: AppState) {
  return state.blotters.scheduledQuotes;
}

export function getColumnDefinition(
  appState: AppState,
): readonly IBlotterColumnDefinition<IScheduledQuotesBlotterItem>[] {
  const state = getScheduledQuotesBlotterState(appState);
  return state.columnDefinition;
}
export function getBlotterPreferenceState(appState: AppState): IBlotterPreferenceState {
  const state = getScheduledQuotesBlotterState(appState);
  if (state.blotterPreferenceState && !state.blotterPreferenceState.pageSizeState) {
    state.blotterPreferenceState.pageSizeState = 25;
  }
  return state.blotterPreferenceState;
}

export function getScheduledQuotesBlotterLoading(state: AppState) {
  return getScheduledQuotesBlotterState(state).loading;
}

export function getScheduledQuotesBlotterError(state: AppState) {
  return getScheduledQuotesBlotterState(state).error;
}

export function canSaveBlotterPreferences(state: AppState): boolean {
  return getScheduledQuotesBlotterState(state).canSaveBlotterPreferences;
}
export function canDisplayDetail({
  blotters: {
    scheduledQuotes: { selectedRowId },
  },
}: AppState) {
  return isDefined(selectedRowId);
}
export function getSelectedItem(state: AppState) {
  const scheduledQuotesState = getScheduledQuotesBlotterState(state);
  return scheduledQuotesState.items.find(run => run.id === scheduledQuotesState.selectedRowId);
}
export function getSelectedPanelTab({
  blotters: {
    scheduledQuotes: { selectedPanelTab },
  },
}: AppState) {
  return selectedPanelTab;
}

export function getFilters(state: AppState): IScheduledQuotesBlotterFilters {
  const scheduledQuotesState = getScheduledQuotesBlotterState(state);
  return {
    quickSearchTerm: scheduledQuotesState.filters?.quickSearchTerm,
    activityFilter: scheduledQuotesState.filters?.activityFilter,
  };
}

const getSortColumns: (state: AppState) => Sort[] = createSelector(
  [(s: AppState) => s.blotters.scheduledQuotes.blotterPreferenceState.columnState],
  columns => {
    const result =
      columns
        ?.filter(c => c.sort === 'asc' || c.sort === 'desc')
        .map(({ colId, sort }) => ({ column: colId!, direction: sort! })) ?? [];
    return result;
  },
);

export function getPaginationInfo(appState: AppState) {
  const state = getScheduledQuotesBlotterState(appState);
  return {
    page: state.page,
    itemsPerPage: state.blotterPreferenceState.pageSizeState,
    totalItems: state.totalItems,
    totalPages: state.totalPages,
  };
}

export function getItems({
  blotters: {
    scheduledQuotes: { items },
  },
}: AppState) {
  return items;
}

export const getBlotterRequest: (state: AppState) => IScheduledQuotesBlotterRequest = createSelector(
  [
    (s: AppState) => s.blotters.scheduledQuotes.page,
    (s: AppState) => s.blotters.scheduledQuotes.blotterPreferenceState.pageSizeState,
    (s: AppState) => s.blotters.scheduledQuotes.filters.quickSearchTerm,
    (s: AppState) => s.blotters.scheduledQuotes.filters.activityFilter,
    getUserPreferencesLanguage,
    getSortColumns,
  ],
  (page, pageSize, quickSearchTerm, activityFilter, lang, sort) => {
    return {
      activityScope: activityFilter,
      fullTextSearch: quickSearchTerm ? translateText(lang, 'en', quickSearchTerm) : undefined,
      page: page - 1,
      size: pageSize ?? 25,
      sort,
    };
  },
);

export const getEditableScheduledRun = (state: AppState): ScheduledRun => {
  return state.scheduledRun.runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID];
};

export const getEditableScheduledRunStatus = (state: AppState): ScheduleRunStatus | undefined => {
  return asSuccess(state.scheduledRun.runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID].runs)?.content.find(run => run.isSelected)
    ?.status;
};
export const getEditableScheduledRunId = (state: AppState): string | undefined => {
  return asSuccess(state.scheduledRun.runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID].runs)?.content.find(run => run.isSelected)
    ?.id;
};

const emptyArray: readonly [] = [];

export const getEditableScheduledRunQuotes = (state: AppState): readonly QuoteInfo[] => {
  return (
    asSuccess(state.scheduledRun.runs[EDITABLE_SCHEDULED_RUN_BLOTTER_ID].runs)?.content.find(run => run.isSelected)
      ?.quotes ?? emptyArray
  );
};

export const canReloadScheduledRun = (state: AppState): boolean => {
  const quotes = getEditableScheduledRunQuotes(state);
  return quotes.length > 0 && quotes.every(quote => isDefined(quote.productType));
};
